import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ModulesContext = createContext(null);

/**
 * Centralised source of truth for which modules are enabled for the current
 * tenant. Pulls from /api/settings/business-activities once per login and
 * caches the result. Pages that need to react to module changes should call
 * refresh() after a save.
 *
 * Shape of `moduleMap` per module code:
 *   { enabled, recommended, included_in_plan, source, permission, tier, ... }
 *
 * Backward compatibility: when the backend returns `legacy_mode === true`
 * (tenants that have never run the activities picker), isEnabled() returns
 * true for EVERY requested module so the existing Al-Bareeq sidebar keeps
 * working exactly as before.
 */
export function ModulesProvider({ children }) {
  const { token, user } = useAuth();
  const [config, setConfig] = useState(null);  // raw response
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!token || !user) return;
    // Super admin / drivers / customers don't need tenant module filtering
    if (user.is_super_admin || user.role === 'driver') { setConfig(null); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/settings/business-activities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConfig(data);
    } catch (_) {
      setConfig(null);  // fail-open: let existing permission system decide
    }
    setLoading(false);
  }, [token, user]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => {
    const legacy = !config || config.legacy_mode;
    const moduleMap = {};
    const permissionMap = {};  // permission -> enabled module exists?
    if (config?.modules) {
      config.modules.forEach((m) => {
        moduleMap[m.code] = m;
        if (m.enabled) permissionMap[m.permission] = true;
      });
    }
    return {
      config,
      legacyMode: legacy,
      moduleMap,
      activities: config?.activities || [],
      loading,
      refresh,
      /** True if the given module code is enabled for this tenant. */
      isModuleEnabled: (code) => legacy || moduleMap[code]?.enabled === true,
      /**
       * True if ANY enabled module uses this permission key. Used by the
       * existing permission-based sidebar as an additional "do we even show
       * this nav item" filter. In legacy mode this always returns true.
       */
      isPermissionActive: (perm) => legacy || permissionMap[perm] === true,
    };
  }, [config, loading, refresh]);

  return <ModulesContext.Provider value={value}>{children}</ModulesContext.Provider>;
}

export function useModules() {
  const ctx = useContext(ModulesContext);
  if (!ctx) {
    // Running outside a provider: assume legacy mode (don't filter anything)
    return {
      config: null, legacyMode: true, moduleMap: {}, activities: [], loading: false,
      refresh: () => {},
      isModuleEnabled: () => true,
      isPermissionActive: () => true,
    };
  }
  return ctx;
}
